import * as PIXI from 'pixi.js';
import * as TweenJS from "slotworks/utils/tween";
import * as particles from "pixi-particles";
import Contract from "slotworks/utils/contract";
import Layers from "slotworks/graphics/layers/layers";
import { Container, BitmapText, Sprite } from "pixi.js";
import { CenterPivot, PIXIElement, Ungroup } from "slotworks/graphics/pixi/group";
import Point from "slotworks/utils/geom/point";
import currency from "slotworks/i18n/currency";
import graphicsManager from "slotworks/graphics/graphics-manager";
import gameState from "slotworks/model/game-state";
import { sequence, parallel } from "slotworks/utils/race-utils";
import { Easing } from "slotworks/utils/tween";
import Timer from "slotworks/utils/timer";
import Rectangle from "slotworks/utils/geom/rectangle";
import soundManager from "slotworks/sound/sound-manager";
import deviceInfo from "slotworks/utils/device-info";
import canvasManager from "slotworks/graphics/canvas/canvas-manager";

const GROUP_NAME = "multiplier_prompt";

export class RespinAwardPrompt {
    
    public container: Container;
    private value: BitmapText;
    private spark: PIXI.Sprite = null;
    private bgSpark: PIXI.Sprite = null;
    private bgSparkTween: TweenJS.Tween;

    private type: string;

    protected explode: particles.Emitter; 
    protected glitterLine: particles.Emitter;

    private glowScale: number = 1;

    private glows: PIXI.Sprite[] = [];

    private movingUp: boolean = false;
    private done: boolean = false;

    public init(type: string) {
        this.type = type;
        this.value = Layers.Prompts.getBitmapText("value");
        this.value.text = "0";
        this.explode = Layers.Prompts.getParticle("glitter_explosion_medium");
        this.explode.emit = false;
        this.glitterLine = Layers.Prompts.getParticle("glitter_trail");
        this.glitterLine.emit = false;
        const background = Layers.Prompts.getSprite("respins_prompt_base") || Layers.Prompts.getSprite("respins_prompt_fs");
        background.anchor.set(0.5, 0.5);
        this.container = new PIXI.Container();
        Layers.Prompts.container.addChildAt(this.container, 0);
        this.container.position.set(background.x + background.width/2, background.y + background.height/2);
        background.position.set(0, 0);
        this.container.addChild(background);
        // @ts-ignore
        this.value.anchor.set(0.5, 0.5);
        this.value.position.set(0, 0);
        this.container.addChild(this.value);

        this.container.y = Layers.Prompts.globalToLocal(new Point(0, 1380)).y;
        this.container.visible = false;
        deviceInfo.onLayoutChange.add(this.onLayoutChange);
    }

    public show() {        
        return new Contract(resolve => {

            this.movingUp = true;
            this.container.visible = true;
            const target = Layers.Prompts.getPosition("prompt");
            const targetY = target.y + target.height/2;
            new TweenJS.Tween(this.container)
            .to({y: targetY}, 400)
            .onComplete(resolve)
            .start();
            this.bgSpark = graphicsManager.createSprite("coin_shine");
            this.bgSpark.anchor.set(0.5, 0.5);
        });
    }

    public countUpAwards(pots: Sprite[], amounts: number[]) {
        let currentValue = 0;
        const funcs = [];
        const record = gameState.getCurrentGame().getCurrentRecord();
        let index = 0;

        this.stopAllGlowTweens();

        while(index < pots.length) {
            const from = currentValue;
            const to = currentValue + amounts[index];
            const pot = pots[index];
            const point = Layers.Custom.get("Respin").localToLocal(Layers.Prompts, new Point(pot.x, pot.y));
            const idx = index;
            funcs.push(() => this.glowPot(idx, pot));
            funcs.push(() => this.moveSparkFromPotToAward(pot));
            funcs.push(() => Contract.wrap(() => {
                this.bouncePlaque();
                this.explode.updateSpawnPos(this.container.x, this.container.y);
                this.explode.cleanup();
                this.explode.emit = true;
            }));
            funcs.push(() => this.countValue(from, to));
            funcs.push(() => Contract.getTimeoutContract(150));
            if(index + 1 < pots.length) {
                const idx = index;
                funcs.push(() => this.movePlaque(idx, 80));
            }
            currentValue = to;
            index++;
        }
        if(record.creditsWon > currentValue) {
            funcs.push(() => this.countValue(currentValue, record.creditsWon));
        }
        funcs.push(() => Contract.wrap(() => {
            this.movingUp = false;
            this.done = true;
        }));
        return sequence(funcs);
    }

    public emphasizePots(pots: Sprite[]) {
        return new Contract(resolve => {
            this.glows = [];
            const sparkPosition = Layers.Custom.get("Respin").getPosition("small_pot_shine");
            for(let i = 0; i < pots.length; i++) {
                const pot = pots[i];
                const spark = graphicsManager.createSprite("small_pot_shine");
                spark.visible = false;
                spark.anchor.set(0.5, 0.5);
                spark.position.set(pot.x, pot.y);
                pot.parent.addChildAt(spark, 0);
                spark.width = sparkPosition.width;
                spark.height = sparkPosition.height;
                this.glows[i] = spark;
            }
            this.glowScale = this.glows[0].scale.x;
            const funcs = [];
            for(let i = 0; i < pots.length; i++) {
                funcs.push(() => this.startGlowTweens(this.glows[i], (pots.length - i) * 100));
            }
            soundManager.customEvent("jp_stack_highlight");
            parallel(funcs).then(() => {
                Timer.setTimeout(resolve, 400);
            });
        })
    }

    public moveAwardToJackpot() {
        return new Contract(resolve => {
            const winValue = Layers.CelebrationForeground.getBitmapText("value");
            const target = Layers.CelebrationForeground.localToLocal(Layers.Prompts, new Rectangle(winValue.x, winValue.y, 0, 0));
            
            new TweenJS.Tween(this.container)
            .to({x: target.x, y: target.y}, 400)
            .onComplete(() => {
                soundManager.customEvent("jp_jackpot_win");
                const spine = Layers.CelebrationContent.getSpineSequence("a_sequence_Jackpot");
                spine.play();
                winValue.text = this.value.text;
                winValue.visible = true;

                this.value.visible = false;
                resolve(null);
            })
            .start();
            
            new TweenJS.Tween(this.value.scale)
            .to({x: 1.1, y: 1.1}, 400)
            .start();

            const elementsToFade = this.container.children.filter((el) => el != this.value);
            const obj = { alpha: 1 }
            const update = () => {
                for(let el of elementsToFade) {
                    el.alpha = obj.alpha;
                }
            }
            new TweenJS.Tween(obj)
            .to({alpha: 0}, 200)
            .onUpdate(update)
            .start();
        })
    }

    private startGlowTweens(glow: Sprite, delay: number) {
        const scale = this.glowScale;
        return new Contract(resolve => {
            Timer.setTimeout(() => {
                glow.visible = true;
                new TweenJS.Tween(glow)
                .to({ rotation: Math.PI * 2 }, 5000)
                .repeat(Infinity)
                .start();
                glow.scale.set(0, 0);
                const obj = { scale: 0};
                new TweenJS.Tween(obj)
                .to({x: [1.1, 1], y: [1.1, 1]}, 400)
                .onUpdate(() => {
                    const scale = obj.scale * this.glowScale;
                    glow.scale.set(scale, scale);
                })
                .start();
            
                new TweenJS.Tween(glow.scale)
                .to({x: [scale + 0.1, scale], y: [scale + 0.1, scale]}, 400)
                .start();
                resolve(null);
            }, delay);
        });
    }

    private stopAllGlowTweens() {
        for(let i = 0; i < this.glows.length; i++) {
            const glow = this.glows[i];
            new TweenJS.Tween(glow.scale)
            .to({x: 0, y: 0}, 200)
            .onComplete(() => glow.visible = false)
            .start();
        }
    }

    private glowPot(index: number, pot: Sprite) {
        const scale = this.glowScale;
        return new Contract(resolve => {
            soundManager.customEvent("jp_multiplier_highlight_" + index);
            const glow = this.glows[index];
            glow.visible = true;
            TweenJS.removeAll(glow.scale);
            new TweenJS.Tween(glow.scale)
            .to({x: [scale + 0.1, scale], y: [scale + 0.1, scale]}, 400)
            .onComplete(() => {
                pot.alpha = 0.5;
                glow.visible = false;
                TweenJS.removeAll(glow);
                resolve(null);
            })
            .start();
        })
    }

    private movePlaque(index: number, length: number) {
        return new Contract(resolve => {
            const obj = {y: 0};
            new TweenJS.Tween(obj)
            .to({y: 1}, 200)
            .onUpdate(() => {
                const target = Layers.Prompts.getPosition("prompt");
                const targetY = target.y + target.height/2;
                const start = targetY - index * length;
                this.container.y = start - length * obj.y;
            })
            .onComplete(resolve)
            .start();
        })
    }

    private centered = false;
    public celebrate() {
        return new Contract(resolve => {
            const center = Layers.Prompts.getPosition("center");
            new TweenJS.Tween(this.container.scale)
            .to({x: 1.5, y: 1.5}, 400)
            .start();
            new TweenJS.Tween(this.container)
            .to({x: center.x + center.width/2, y: center.y + center.height/2}, 400)
            .onComplete(() => {
                this.centered = true;
                const center = Layers.Prompts.getPosition("center");
                this.container.position.set(center.x + center.width/2, center.y + center.height/2);
                resolve(null);
            })
            .start();
        }) 
    }

    public fadeToBase() {
        return new Contract(resolve => {
            resolve(null);
        })
    }

    public destroy() {
        for(let glow of this.glows) {
            TweenJS.removeAll(glow);
            TweenJS.removeAll(glow.scale);
        }
        deviceInfo.onLayoutChange.remove(this.onLayoutChange);
    }

    public countValue(from: number, to: number) {
        return new Contract(resolve => {
            soundManager.customEvent("jp_award_tally");
            const obj = { value: from };
            const update = () => {
                this.value.text = currency.format(obj.value, undefined, undefined, true, true);
            }
            new TweenJS.Tween(obj)
            .to({ value: to }, 400)
            .onUpdate(update)
            .onComplete(resolve)
            .start();
        });
    }

    private bouncePlaque() {
        new TweenJS.Tween(this.container.scale)
        .to({x: 1.1, y: 1.1}, 150)
        .easing(Easing.Circular.Out)
        .yoyo(true)
        .repeat(1)
        .start();
    }

    private moveSparkFromPotToAward(pot: PIXI.Sprite) {
        return new Contract(resolve => {
            this.glitterLine.emit = true;
            soundManager.customEvent("jp_multiplier_add");
            const point = Layers.Custom.get("Respin").localToLocal(Layers.Prompts, new Point(pot.x, pot.y));
            const obj = {x: point.x, y: point.y};
            new TweenJS.Tween(obj)
            .to({x: this.container.x, y: this.container.y}, 400)
            .onUpdate(() => {
                this.glitterLine.updateSpawnPos(obj.x, obj.y);
            })
            .onComplete(() => {
                resolve(null);
                this.glitterLine.emit = false;
            }).start();          
        });
    }

    private onLayoutChange = () => {
        setTimeout(() => {
            if(this.centered) {
                const center = Layers.Prompts.getPosition("center");
                this.container.position.set(center.x + center.width/2, center.y + center.height/2);
            }
            
            const sparkPosition = Layers.Custom.get("Respin").getPosition("small_pot_shine");
            for(let i = 0; i < this.glows.length; i++) {
                const potPosition = Layers.Custom.get("Respin").getPosition(`pot_grand_${i + 1}`);
                const spark = this.glows[i];
                spark.width = sparkPosition.width;
                spark.height = sparkPosition.height;
                spark.position.set(potPosition.x + potPosition.width/2, potPosition.y + potPosition.height/2);
            }
            this.glowScale = this.glows[0].scale.x;
        }, 10);
    }
}


// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./src/components/respin/respin-award-prompt.ts